import {
  HVAC_EFFICIENCY_CONSTANTS,
  useSeerRating,
  useHVACSensorsByGroup,
  getSeerLevelsFromSensor,
  useHistoricalSeerRating,
  determineEERDisplayMode,
  POLLING_INTERVALS_MILLIS,
} from 'common';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { YStack, Text, XStack, Spinner } from 'tamagui';
import { tokens } from 'tamagui-config/src/tokens';

import { SEERGauge } from './SeerGauge';
import { useAppSync } from '../../../contexts/AppSyncContext';

export const HVACEfficiencyContent: React.FC = () => {
  const { t } = useTranslation();
  const { selectedHubId } = useAppSync();
  const { data: seerData, loading, fetchSeerRating } = useSeerRating();
  const { lastGoodSeerRating, fetchLastGoodSeerRating } = useHistoricalSeerRating();

  useEffect(() => {
    if (selectedHubId) {
      fetchSeerRating(selectedHubId);

      // TODO: Subscription not working
      const interval = setInterval(() => {
        fetchSeerRating(selectedHubId);
      }, POLLING_INTERVALS_MILLIS.HVAC);

      return () => clearInterval(interval);
    }
  }, [selectedHubId, fetchSeerRating]);

  useEffect(() => {
    if (selectedHubId && seerData) {
      const seerValue = seerData?.lastMinuteSeerRating?.seerValue ?? '-';
      const lastHourRating = seerData?.previousHourRating?.seerValue ?? '-';
      const hvacRunning = seerData?.hvacRunning ?? false;

      const isCurrentSeerInvalid =
        seerValue === '-' ||
        seerValue === '0' ||
        (typeof seerValue === 'number' && seerValue === 0);

      const isLastHourInvalid =
        lastHourRating === '-' ||
        lastHourRating === '0' ||
        (typeof lastHourRating === 'number' && lastHourRating === 0);

      const shouldFetchHistorical = hvacRunning && isCurrentSeerInvalid && isLastHourInvalid;

      if (shouldFetchHistorical) {
        fetchLastGoodSeerRating(selectedHubId);
      }
    }
  }, [selectedHubId, seerData, fetchLastGoodSeerRating]);

  const { hvacSensorGroups } = useHVACSensorsByGroup(selectedHubId ?? undefined);
  const hvacSensors = hvacSensorGroups.flatMap((group) =>
    [group.indoorSensor, group.outdoorSensor].filter(Boolean),
  );

  const seerLevels = getSeerLevelsFromSensor(hvacSensors);
  const lastMinuteSeerRating = seerData?.lastMinuteSeerRating?.seerValue;
  const previousHourRating = seerData?.previousHourRating?.seerValue;
  const hvacRunning = seerData?.hvacRunning ?? false;
  const displayMode = determineEERDisplayMode(seerData?.lastMinuteSeerRating);

  const canCalculateCurrentEer = (() => {
    if (!lastMinuteSeerRating || lastMinuteSeerRating === '-' || !hvacRunning) {
      return false;
    }

    const numValue =
      typeof lastMinuteSeerRating === 'number'
        ? lastMinuteSeerRating
        : parseFloat(String(lastMinuteSeerRating));

    return !isNaN(numValue) && numValue !== 0;
  })();

  const lastEerRating = seerData?.runningHourSeerRating?.seerValue ?? '-';

  const lastHourEerRating =
    lastGoodSeerRating?.seerValue ?? seerData?.previousHourRating?.seerValue ?? '-';

  const historicalTimestamp = lastGoodSeerRating?.timestamp;

  const lastMinuteSeerRatingNum =
    typeof lastMinuteSeerRating === 'string' && lastMinuteSeerRating !== '-'
      ? parseFloat(lastMinuteSeerRating)
      : 0;
  const previousHourRatingNum =
    typeof previousHourRating === 'string' && previousHourRating !== '-'
      ? parseFloat(previousHourRating)
      : 0;

  const valueDifference =
    lastMinuteSeerRatingNum && previousHourRatingNum
      ? lastMinuteSeerRatingNum - previousHourRatingNum
      : undefined;

  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading && !seerData) {
    return (
      <YStack flex={1} justifyContent="center" alignItems="center">
        <Spinner size="large" color={tokens.color.white12} />
      </YStack>
    );
  }

  return (
    <YStack position="relative" flex={1}>
      {historicalTimestamp && !canCalculateCurrentEer && (
        <Text
          position="absolute"
          top="$2"
          right="$3"
          fontSize="$3"
          color={tokens.color.gray10Light}
          textAlign="right"
          zIndex={10}>
          {formatTimestamp(historicalTimestamp)}
        </Text>
      )}
      <XStack flex={1} justifyContent="space-between" gap={'$8'}>
        <SEERGauge
          value={lastMinuteSeerRatingNum < 1 ? 0 : lastMinuteSeerRatingNum || 0}
          minValue={seerLevels.minValue}
          midValue={seerLevels.midValue}
          maxValue={seerLevels.maxValue}
          size={HVAC_EFFICIENCY_CONSTANTS.GAUGE.DEFAULT_SIZE}
          hvacRunning={hvacRunning && displayMode.mode !== 'off'}
          valueDifference={valueDifference}
        />
        <YStack flex={1} justifyContent="center" alignItems="center" gap="$4">
          {(() => {
            if (displayMode.mode === 'peak') {
              const peakValue = lastHourEerRating && lastHourEerRating !== '-' ? lastHourEerRating : previousHourRating;
              if (peakValue && peakValue !== '-') {
                return (
                  <YStack
                    background={tokens.color.transparentBorder}
                    gap={'$2'}
                    padding="$4"
                    borderRadius="$4"
                    width="100%">
                    <Text fontSize={'$6'} color={tokens.color.white12} textAlign="center">
                      Last Peak EER
                    </Text>
                    <XStack alignItems="center" justifyContent="center" gap={'$1'}>
                      <Text
                        fontFamily={'$heading'}
                        fontSize="$6"
                        fontWeight={'$6'}
                        color={tokens.color.color12}>
                        {peakValue}
                      </Text>
                    </XStack>
                  </YStack>
                );
              }
            } else if (displayMode.mode === 'instant') {
              const avgValue = lastEerRating && lastEerRating !== '-' ? lastEerRating : lastHourEerRating;
              if (avgValue && avgValue !== '-') {
                return (
                  <YStack
                    background={tokens.color.transparentBorder}
                    gap={'$2'}
                    padding="$4"
                    borderRadius="$4"
                    width="100%">
                    <Text fontSize={'$6'} color={tokens.color.white12} textAlign="center">
                      Avg EER
                    </Text>
                    <XStack alignItems="center" justifyContent="center" gap={'$1'}>
                      <Text
                        fontFamily={'$heading'}
                        fontSize="$6"
                        fontWeight={'$6'}
                        color={tokens.color.color12}>
                        {avgValue}
                      </Text>
                    </XStack>
                  </YStack>
                );
              }
            }
            return null;
          })()}
        </YStack>
      </XStack>
      <Text fontSize="$9.5" color={tokens.color.white12} marginTop="$5" textAlign="center">
        {t('hvac.seerRating')}
      </Text>
    </YStack>
  );
};
